// Active → Complete: archive event; optional list of devices not yet retrieved.

import React, { JSX, useEffect, useMemo, useState } from 'react';
import EventStatusTransitionDialogFrame from './EventStatusTransitionDialogFrame';
import DeviceConfigureForm from '../../DeviceConfigureForm/DeviceConfigureForm';

type Props = {
  open: boolean;
  eventId: string;
  raceId: string;
  eventName?: string;
  /** Shown as subtitle (e.g. race name). */
  subtitleOverride?: string;
  nonRecalledDeviceIds: string[];
  busy: boolean;
  onCancel: () => void;
  onProceed: () => void | Promise<void>;
};

export default function ActiveToCompleteConfirmDialog(props: Props): JSX.Element | null {
  const {
    open,
    eventName,
    eventId,
    raceId,
    subtitleOverride,
    nonRecalledDeviceIds,
    busy,
    onCancel,
    onProceed,
  } = props;
  const titleName =
    (subtitleOverride?.trim() ? subtitleOverride.trim() : null) ||
    (eventName?.trim() ? eventName : eventId);
  const n = nonRecalledDeviceIds.length;
  const allRecalled = n === 0;

  const sortedIds = useMemo(() => [...nonRecalledDeviceIds].sort((a, b) => a.localeCompare(b)), [nonRecalledDeviceIds]);
  const [noteTargetDeviceId, setNoteTargetDeviceId] = useState<string | null>(null);
  const [lifecycleTargetDeviceId, setLifecycleTargetDeviceId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setNoteTargetDeviceId(null);
      setLifecycleTargetDeviceId(null);
    }
  }, [open]);

  return (
    <EventStatusTransitionDialogFrame
      open={open}
      busy={busy}
      onCancel={onCancel}
      onProceed={onProceed}
      titleId="active-to-complete-title"
      eyebrow="Race completion"
      title="Move to Complete"
      subtitle={titleName}
      headerAccent={allRecalled ? 'emerald' : 'amber'}
      warningTitle="This operation cannot be undone"
      warningBody={
        <>
          Completing an Active race keeps assignment history and frees race device locks by default. This race becomes
          read-only for configuration and assignment changes.
        </>
      }
      operationIntro={
        <>
          <p>
            You are closing out <span className="font-semibold text-gray-800 dark:text-slate-200">Active</span> work and marking{' '}
            this race <span className="font-semibold text-gray-800 dark:text-slate-200">Complete</span>
            . Use this when the race is finished and device retrieval is handled to your satisfaction.
          </p>
          {!allRecalled ? (
            <p>
              <span className="font-semibold text-gray-800 dark:text-slate-200">{n}</span> device{n === 1 ? ' on this race is' : 's on this race are'} not
              marked <span className="font-semibold text-gray-800 dark:text-slate-200">retrieved</span>. You can still complete{' '}
              this race; use <span className="font-semibold text-gray-800 dark:text-slate-200">Retrieve devices</span>{' '}
              first if you want every unit checked in before closing.
            </p>
          ) : (
            <p className="flex items-start gap-2 text-sm font-medium text-emerald-900">
              <span
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-200 text-[10px] font-bold text-emerald-900"
                aria-hidden
              >
                ✓
              </span>
              All assigned devices are marked retrieved.
            </p>
          )}
        </>
      }
      confirmPhrase="COMPLETE"
      confirmInputId="active-to-complete-confirm-input"
      proceedButtonText="Proceed to Complete"
      proceedUseSuccessTone={allRecalled}
    >
      {!allRecalled ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-gradient-to-b from-sky-50 via-white to-white dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 p-4 shadow-sm ring-1 ring-gray-200 dark:ring-slate-700">
          <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400">Not retrieved</h3>
          <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">Device IDs still on the course or in the field.</p>
          <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-inner">
            <ul className="max-h-52 divide-y divide-slate-100 overflow-y-auto text-xs">
              {sortedIds.map((id) => (
                <li
                  key={id}
                  className="flex flex-wrap items-center justify-between gap-2 break-all px-3 py-2 font-mono text-gray-900 dark:text-slate-100"
                >
                  <span className="min-w-0 flex-1">{id}</span>
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-700 dark:text-slate-300 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
                    onClick={() => {
                      setNoteTargetDeviceId(null);
                      setLifecycleTargetDeviceId(id);
                    }}
                    disabled={busy}
                    title="Open Submit lifecycle report dialog for this device"
                  >
                    Lifecycle
                  </button>
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-700 dark:text-slate-300 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
                    onClick={() => {
                      setLifecycleTargetDeviceId(null);
                      setNoteTargetDeviceId(id);
                    }}
                    disabled={busy}
                    title="Open Leave note dialog for this device"
                  >
                    Add note
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      {noteTargetDeviceId ? (
        <DeviceConfigureForm
          selectedIds={[noteTargetDeviceId]}
          initialOperation="LEAVE_DEVICE_NOTE"
          overlayZIndexClass="z-[5400]"
          initialNoteEventId={eventId}
          onClose={() => setNoteTargetDeviceId(null)}
        />
      ) : null}
      {lifecycleTargetDeviceId ? (
        <DeviceConfigureForm
          selectedIds={[lifecycleTargetDeviceId]}
          initialOperation="SUBMIT_LIFECYCLE_REPORT"
          overlayZIndexClass="z-[5400]"
          initialLifecycleEventId={eventId}
          initialLifecycleRaceId={raceId}
          onClose={() => setLifecycleTargetDeviceId(null)}
        />
      ) : null}
    </EventStatusTransitionDialogFrame>
  );
}
